import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Database, Download, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';

export const AdminBackupPage = () => {
  const [stats, setStats] = useState({ collections: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getBackupStats();
      setStats(res.data);
    } catch (e) {
      toast.error('Failed to load stats');
    } finally { setLoading(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await adminAPI.downloadBackup();
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `matrise-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) {
      toast.error('Backup failed');
    } finally { setDownloading(false); }
  };

  const restore = async () => {
    if (!selectedFile) return toast.error('Choose a backup file first');
    if (confirmText !== 'RESTORE') return toast.error('Type RESTORE to confirm');
    setRestoring(true);
    setRestoreResult(null);
    try {
      const res = await adminAPI.restoreBackup(selectedFile);
      setRestoreResult(res.data);
      toast.success('Restore complete');
      setSelectedFile(null);
      setConfirmText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Restore failed');
    } finally { setRestoring(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-backup-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Backup & Restore</h1>
          <p className="mt-2 text-muted-foreground">Download a full snapshot of the database or restore from a previous backup file.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> Database Snapshot</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Total documents</p>
                <p className="text-3xl font-semibold mt-1" data-testid="total-docs">{loading ? '—' : stats.total.toLocaleString()}</p>
              </div>

              <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border" data-testid="collections-list">
                {Object.entries(stats.collections).map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between px-4 py-2">
                    <span className="text-sm font-mono">{name}</span>
                    <Badge variant="outline">{count.toLocaleString()}</Badge>
                  </div>
                ))}
              </div>

              <Button onClick={download} disabled={downloading} className="w-full" data-testid="download-backup-btn">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Preparing...' : 'Download Backup (.json)'}
              </Button>
              <p className="text-xs text-muted-foreground">
                The backup is a single JSON file containing all users, wallets, transactions, currencies, vouchers, tickets, predictions and more.
              </p>
            </CardContent>
          </Card>

          <Card className="border-red-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Restore from Backup</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-700 dark:text-red-400">
                <strong>Destructive action.</strong> Restoring will wipe existing data and replace it with the file&apos;s contents. Your current admin account is preserved so you don&apos;t get locked out.
              </div>

              <div>
                <label className="text-sm font-medium">Backup file (.json)</label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  data-testid="restore-file-input"
                  className="mt-1"
                />
                {selectedFile && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Type <span className="font-mono">RESTORE</span> to confirm</label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESTORE"
                  data-testid="restore-confirm-input"
                  className="mt-1"
                />
              </div>

              <Button
                onClick={restore}
                disabled={restoring || !selectedFile || confirmText !== 'RESTORE'}
                variant="destructive"
                className="w-full"
                data-testid="restore-backup-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {restoring ? 'Restoring...' : 'Restore Now'}
              </Button>

              {restoreResult && (
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs" data-testid="restore-result">
                  <p className="font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mb-1">
                    <CheckCircle2 className="h-3 w-3" /> Restore complete
                  </p>
                  <ul className="space-y-0.5 text-muted-foreground">
                    {Object.entries(restoreResult.restored || {}).map(([n, c]) => (
                      <li key={n}>{n}: {c} documents</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};
